import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Alert, Button } from "antd";

import { ApiError } from "../api/constants";
import { onboardingUnsubscribe } from "../api/mailing";
import AuthCard from "../components/auth/AuthCard";
import AppFooter from "../components/layout/AppFooter";
import { usePageTitle } from "../utils/usePageTitle";
import { usePendingAction } from "../utils/usePendingAction";

export const OnboardingUnsubscribePage = () => {
  const { hash } = useParams<{ hash: string }>();
  return <OnboardingUnsubscribeConfirmation key={hash} hash={hash ?? ""} />;
};

export const OnboardingUnsubscribeConfirmation = ({
  hash,
  onUnsubscribe = onboardingUnsubscribe,
}: {
  hash: string;
  onUnsubscribe?: (hash: string) => Promise<void>;
}) => {
  const { t } = useTranslation();
  usePageTitle(t("mailing.onboardingUnsubscribeTitle"));
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);
  const [errorKey, setErrorKey] = useState("");
  const { pending, run } = usePendingAction();

  const handleConfirm = () =>
    run(async () => {
      setErrorKey("");
      try {
        await onUnsubscribe(hash);
        setIsUnsubscribed(true);
      } catch (error) {
        setErrorKey(
          error instanceof ApiError && error.status === 404
            ? "mailing.invalidLink"
            : "errors.shared.common_error",
        );
      }
    });

  return (
    <div className="entry-layout">
      <AuthCard>
        <h1>{t("mailing.onboardingUnsubscribeTitle")}</h1>
        {isUnsubscribed ? (
          <Alert type="success" showIcon title={t("mailing.haveBeenRemoved")} />
        ) : (
          <>
            <p>{t("mailing.onboardingAreYouSure")}</p>
            <Button
              type="primary"
              size="large"
              loading={pending}
              onClick={handleConfirm}
            >
              {t("mailing.confirm")}
            </Button>
            {errorKey && (
              <Alert
                style={{ marginTop: 16 }}
                type="error"
                showIcon
                title={t(errorKey)}
              />
            )}
          </>
        )}
      </AuthCard>
      <AppFooter />
    </div>
  );
};
